/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { EditorState, Modifier, RichUtils, SelectionState } from 'draft-js';

/**
 * Internal dependencies
 */
import weightFormatter from './formatters/weight';
import italicFormatter from './formatters/italic';
import underlineFormatter from './formatters/underline';
import uppercaseFormatter from './formatters/uppercase';
import { LIST_BLOCK_TYPES } from './constants';
import adjustListDepth from './adjustListDepth';

function getStateFromCommmand(command, oldEditorState, formatterContext) {
  switch (command) {
    case 'bold':
      return weightFormatter.setters.toggleBold(
        oldEditorState,
        undefined,
        formatterContext?.defaultFontWeight
      );

    case 'italic':
      return italicFormatter.setters.toggleItalic(oldEditorState);

    case 'underline':
      return underlineFormatter.setters.toggleUnderline(oldEditorState);

    case 'uppercase':
      return uppercaseFormatter.setters.toggleUppercase(oldEditorState);

    case 'backspace':
    case 'backspace-word':
    case 'backspace-to-start-of-line': {
      const selection = oldEditorState.getSelection();
      if (!selection.isCollapsed() || selection.getStartOffset() !== 0) {
        return null;
      }

      const content = oldEditorState.getCurrentContent();
      const block = content.getBlockForKey(selection.getStartKey());
      if (!LIST_BLOCK_TYPES.includes(block.getType())) {
        return null;
      }

      if (block.getDepth() > 0) {
        return adjustListDepth(oldEditorState, -1);
      }

      return EditorState.push(
        oldEditorState,
        Modifier.setBlockType(content, selection, 'unstyled'),
        'change-block-type'
      );
    }

    default:
      return null;
  }
}

export const getHandleKeyCommandFromState =
  (setEditorState, formatterContext) => (command, currentEditorState) => {
    const newEditorState = getStateFromCommmand(
      command,
      currentEditorState,
      formatterContext
    );
    if (newEditorState) {
      setEditorState(newEditorState);
      return 'handled';
    }
    return 'not-handled';
  };

export function insertListSoftNewline(editorState) {
  const content = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const startKey = selection.getStartKey();

  if (startKey !== selection.getEndKey()) {
    return null;
  }

  const block = content.getBlockForKey(startKey);
  if (!block || !LIST_BLOCK_TYPES.includes(block.getType())) {
    return null;
  }

  return RichUtils.insertSoftNewline(editorState);
}

export function getSelectionForAll(content) {
  const firstBlock = content.getFirstBlock();
  const lastBlock = content.getLastBlock();
  return new SelectionState({
    anchorKey: firstBlock.getKey(),
    anchorOffset: 0,
    focusKey: lastBlock.getKey(),
    focusOffset: lastBlock.getLength(),
  });
}

export function removeListFormatting(editorState) {
  const content = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const startKey = selection.getStartKey();
  let endKey = selection.getEndKey();

  if (startKey !== endKey && selection.getEndOffset() === 0) {
    endKey = content.getKeyBefore(endKey) || endKey;
  }

  let isInSelection = false;
  let hasChanges = false;
  const blockMap = content.getBlockMap().map((block, key) => {
    if (key === startKey) {
      isInSelection = true;
    }

    const shouldRemoveListFormatting =
      isInSelection && LIST_BLOCK_TYPES.includes(block.getType());

    if (key === endKey) {
      isInSelection = false;
    }

    if (!shouldRemoveListFormatting) {
      return block;
    }

    hasChanges = true;
    return block.merge({ type: 'unstyled', depth: 0 });
  });

  if (!hasChanges) {
    return editorState;
  }

  const nextContent = content.merge({
    blockMap,
    selectionBefore: selection,
    selectionAfter: selection,
  });

  return EditorState.push(editorState, nextContent, 'change-block-type');
}

export function getSelectionForOffset(content, offset) {
  const blocks = content.getBlocksAsArray();
  let countdown = offset;
  for (let i = 0; i < blocks.length && countdown >= 0; i++) {
    const block = blocks[i];
    const length = block.getLength();
    if (countdown <= length) {
      const selection = new SelectionState({
        anchorKey: block.getKey(),
        anchorOffset: countdown,
        focusKey: block.getKey(),
        focusOffset: countdown,
      });
      return selection;
    }
    // +1 char for the delimiter.
    countdown -= length + 1;
  }
  return null;
}
